import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
// Self-host the Inter variable font that @repo/theme declares as the body/heading family. Without this
// the app silently fell back to system-ui and never rendered the intended typography. Imported before
// index.css so the @font-face rules land ahead of the Panda layer cascade.
import "@fontsource-variable/inter";
import "./index.css";
import { RouterProvider } from "react-router/dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { setPostHogContextProvider } from "@repo/api-client";
import { AuthProvider } from "@repo/auth";
import { AppErrorBoundary, initOtel, initWebVitals, useAnalytics } from "@repo/analytics";
import { FlagsProvider } from "@repo/flags";
import { applyLanguage, I18nProvider, recallLanguage } from "@repo/i18n";
import { env } from "./env";
import { router } from "./router";
import { ConsentedAnalytics } from "./shell/ConsentedAnalytics";
import { createQueryClient } from "./shell/queryClient";

// The staff shell is French-first (ADR-0013): default to FR regardless of the browser locale, and
// only honour EN when the operator explicitly toggled it (persisted via applyLanguage). The header's
// language switch flips this at any time.
void applyLanguage(recallLanguage() ?? "fr");

// Browser tracing (ADR-0035): OTLP/HTTP to the collector, traceparent propagated to the
// API only, 0.1 sampling mirroring the backend's OTEL_SAMPLING. Safe no-op without the
// endpoint (local dev with no collector running).
initOtel({
  endpoint: env.VITE_OTEL_EXPORTER_OTLP_ENDPOINT,
  sampleRatio: 0.1,
  environment: import.meta.env.MODE,
  propagateTraceUrls: [env.VITE_API_URL],
});
initWebVitals();

const queryClient = createQueryClient();

/**
 * Forwards the PostHog session + distinct id to the api-client so every backend request carries the
 * `X-PostHog-*` headers — the server links its wide-event logs to the Session Replay + person. Reads
 * through the AnalyticsClient port (never posthog-js). Rendered inside AnalyticsProvider.
 */
function PostHogApiLink(): null {
  const analytics = useAnalytics();
  useEffect(() => {
    setPostHogContextProvider(() => ({
      distinctId: analytics.getDistinctId(),
      sessionId: analytics.getSessionId(),
    }));
  }, [analytics]);
  return null;
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("#root not found");

createRoot(rootEl).render(
  <StrictMode>
    {/* ConsentedAnalytics gates PostHog on the cookie-consent grant (CNIL/RGPD): no init, no cookie,
        no capture until the operator accepts. It renders AnalyticsProvider internally + the banner. */}
    <ConsentedAnalytics
      config={{
        apiKey: env.VITE_POSTHOG_PROJECT_TOKEN,
        host: env.VITE_POSTHOG_HOST,
      }}
    >
      {/* Directly inside AnalyticsProvider (ADR-0015): a failure in any provider below is
          still caught and reported through the port instead of white-screening. */}
      <AppErrorBoundary>
        <PostHogApiLink />
        <I18nProvider>
          <FlagsProvider>
            <AuthProvider>
              <QueryClientProvider client={queryClient}>
                <RouterProvider router={router} />
              </QueryClientProvider>
            </AuthProvider>
          </FlagsProvider>
        </I18nProvider>
      </AppErrorBoundary>
    </ConsentedAnalytics>
  </StrictMode>,
);
